import React, { useState, useEffect, useContext } from 'react';
import { useFormikContext } from 'formik';
import { isEmpty } from 'lodash';
import { ArnContext, FormContext } from 'contexts';
import AustralianPassportDetails from './AustralianPassportDetails';
import AustralianBirthCertificateDetails from './AustralianBirthCertificateDetails';
import AustralianCitizenshipCertificateDetails from './AustralianCitizenshipCertificateDetails';
import ForeignPassportDetails from './ForeignPassportDetails';
import ImmiCardDetails from './ImmiCardDetails';
import { IFormAlertType } from 'types/forms/formTypes';
import { FormNavigation } from 'components/CASAForm';
import { useArnService } from 'services';
import { Loader } from 'components/common';
import { useHistory } from 'react-router-dom';
import {
  ArnApplicationDvsMethodFormData,
  IBirthCertificateDetails,
  ICitizenshipCertificateDetails,
  IForeignPassportDetails,
  IImmiCardDetails,
  IPassportDetails,
  IPersonalDetails
} from 'types/forms/arnApplicationTypes';
import {
  getIdentityDocumentTitleAndDataModel,
  isFullNameMatch
} from 'helpers/arnApplicationHelper';
import { IDENTITY_TYPES, STATE_AND_TERRITORY_CODE, DVS_VERIFICATION_RESULT_CODE } from 'arnConstants';
import { IDvsCheckRequest, IDvsCheckResponse } from 'services/arnService/arnServiceTypes';

interface IdentityDetailsProps {
  nextStep: string;
}

const IdentityDetails: React.FC<IdentityDetailsProps> = (props: IdentityDetailsProps) => {
  const { setPreviousStep } = useContext(ArnContext);
  const { values, validateForm, getFieldMeta, setFieldValue } = useFormikContext<ArnApplicationDvsMethodFormData>();
  const { setFormAlert, openStepById, setOnChangeValidation } = useContext(FormContext);
  const identityType = getIdentityDocumentTitleAndDataModel(values.identityType);
  const maxDvsCheckAttempts = 5;
  const arnService = useArnService();
  const [checkDvs, setCheckDvs] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [disableNextBtn, setDisableNextBtn] = useState<boolean>(false);
  const dvsFailureCount = getFieldMeta(`${identityType.identityType}.dvsFailureCount`).value;
  const history = useHistory();

  useEffect(() => {
    const dvsCheckCount = dvsFailureCount ? dvsFailureCount as number + 1 : 1;
    setDisableNextBtn(dvsCheckCount > maxDvsCheckAttempts);
  }, []);

  useEffect(() => {
    const negativeDvsResponse = (dvsCkeckResult: IDvsCheckResponse | undefined | null) => {
      const incrementFailureCount = dvsCkeckResult?.verificationResultCode == 'N' || dvsCkeckResult?.verificationResultCode == 'D' || dvsCkeckResult?.errors;
      const dvsCheckCount = dvsFailureCount ? (incrementFailureCount ? dvsFailureCount as number + 1 : dvsFailureCount as number) : 1;
      setFieldValue(`${identityType.identityType}.dvsFailureCount`, dvsCheckCount);
      const remainingAttempts = maxDvsCheckAttempts - dvsCheckCount;
      setDisableNextBtn(dvsCheckCount >= maxDvsCheckAttempts);

      let alert: IFormAlertType | null = null;

      if (dvsCkeckResult?.verificationResultCode === DVS_VERIFICATION_RESULT_CODE.SYSTEM_ERROR || !dvsCkeckResult) {
        history.push('/dvsserviceunavailable');
      }
      else {
        alert = dvsCheckCount < maxDvsCheckAttempts ? {
          as: 'warning',
          title: 'We were unable to find or match your identification document',
          message: <>
            Please check the details you&apos;ve provided below to make sure they match your identification document before trying again.<br /><br />
            If the issue persists try <a href='' onClick={handleBackPreviousScreen}>another form of identification</a> or you can use <a href='https://www.digitalidentity.gov.au/create-or-manage-your-digital-id' target="_blank" rel="noopener noreferrer">Digital Identity</a> to create your myCASA account and verify your identity.<br /><br />
            Attempts remaining: <strong>{remainingAttempts}</strong><br /><br />
          </>
        }
          : {
            as: 'error',
            title: 'Your identification cannot be used for verification right now',
            message: <>
              Attempts remaining: <strong>0</strong><br /><br />
              You can try <a href='' onClick={handleBackPreviousScreen}>another form of identification</a> or you can use <a href='https://www.digitalidentity.gov.au/create-or-manage-your-digital-id' target="_blank" rel="noopener noreferrer">Digital Identity</a> to create your myCASA account and verify your identity.
              Or you can <a href='' onClick={handleManualUpload}>upload a certified copy of your identification documentation </a> for manual verification.
            </>
          };
      }

      setFormAlert(alert);
    };
    async function devCheck () {
      if (checkDvs) {
        setIsLoading(true);
        setCheckDvs(false);
        setFormAlert(null);
        const dvsCheckData = getDvsCheckRequestModel();
        try {
          const dvsCkeckResult = await arnService.getDvsCheckResult(dvsCheckData);
          const isDvsCheckSuccessful = dvsCkeckResult.dvsResponse;
          const dvsCheckResult = isDvsCheckSuccessful ? 'Passed' : 'Failed';
          setFieldValue(`${identityType.identityType}.dvsValidationIndicator`, dvsCheckResult);
          setIsLoading(false);

          if (dvsCkeckResult.errors || !dvsCkeckResult.dvsResponse) {
            negativeDvsResponse(dvsCkeckResult);
            return;
          }

          if (dvsCkeckResult.dvsResponse) {
            //set date of birth in the personal details
            const dateOfBirth = getFieldMeta(`${identityType.identityType}.dateOfBirth`).value;
            setFieldValue('dateOfBirth', dateOfBirth);
            const successAlert: IFormAlertType = {
              as: 'success',
              title: 'Your identification has been successfully verified'
            };
            setFormAlert(successAlert);
          }

          //Check for name missmatch
          //if any mismatch show PreviousName page
          const identityDetails = getFieldMeta(`${identityType.identityType}`).value;
          const personalDetails: IPersonalDetails = values;
          const isNameMatch = isFullNameMatch(personalDetails, identityDetails);
          const nextStepsId = isNameMatch ? props.nextStep : 'PreviousName';
          openStepById(nextStepsId);
          setPreviousStep('IdentificationDetails');
          return;
        }
        catch (error) {
          negativeDvsResponse(null);
          setIsLoading(false);
          setCheckDvs(false);
        }
      }
    }
    devCheck();
  }, [checkDvs]);

  const handleBackPreviousScreen = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setFormAlert(null);
    openStepById('IdentityVerification');
  };

  const handleManualUpload = (event: React.MouseEvent<HTMLElement>) => {
    event.preventDefault();
    setFieldValue('manualPOIDocuments.isManualUpload', true);
    setFormAlert(null);
    openStepById('ManualPOIInformation');
  };

  const getDvsCheckRequestModel = () => {
    const identityDetails = getFieldMeta(`${identityType.identityType}`).value;
    const dvsCheckRequestModel: IDvsCheckRequest = {
      identityType: values?.identityType ?? '',
      familyName: '',
      givenNames: '',
      dateOfBirth: '',
      passportNumber: '',
      registrationNumber: '',
      registrationDate: '',
      acquisitionDate: '',
      certificateNumber: '',
      immiCardNumber: '',
      previousFamilyName: '',
      previousGivenNames: '',
      registrationState: '',
      registrationYear: '',
      certificateIssueDate: '',
      secondPersonFamilyName: '',
      secondPersonGivenNames: '',
      dateOfEvent: ''
    };

    if (values?.identityType === IDENTITY_TYPES.AU_PASSPORT) {
      const passportDetails = identityDetails as IPassportDetails;
      dvsCheckRequestModel.identityType = IDENTITY_TYPES.AU_PASSPORT;
      dvsCheckRequestModel.passportNumber = passportDetails?.passportNumber;
      dvsCheckRequestModel.familyName = passportDetails?.familyName;
      dvsCheckRequestModel.givenNames = passportDetails?.givenNames;
      dvsCheckRequestModel.dateOfBirth = passportDetails?.dateOfBirth;

      return dvsCheckRequestModel;
    }
    if (values?.identityType === IDENTITY_TYPES.AU_BIRTH_CERTIFICATE) {
      const birthCertificateDetails = identityDetails as IBirthCertificateDetails;
      dvsCheckRequestModel.identityType = IDENTITY_TYPES.AU_BIRTH_CERTIFICATE;
      dvsCheckRequestModel.familyName = birthCertificateDetails?.familyName;
      dvsCheckRequestModel.givenNames = birthCertificateDetails?.givenNames;
      dvsCheckRequestModel.dateOfBirth = birthCertificateDetails.dateOfBirth;
      dvsCheckRequestModel.registrationState = birthCertificateDetails.stateOrTerritory;
      dvsCheckRequestModel.registrationNumber = birthCertificateDetails.registrationNumber;
      if (birthCertificateDetails.stateOrTerritory == STATE_AND_TERRITORY_CODE.TAS
        || birthCertificateDetails.stateOrTerritory == STATE_AND_TERRITORY_CODE.QLD) {
        dvsCheckRequestModel.registrationDate = birthCertificateDetails.dateOfRegistration;
      }

      return dvsCheckRequestModel;
    }
    if (values?.identityType === IDENTITY_TYPES.AU_CITIZENSHIP_CERTIFICATE) {
      const citizenshipCertificateDetails = identityDetails as ICitizenshipCertificateDetails;
      dvsCheckRequestModel.identityType = IDENTITY_TYPES.AU_CITIZENSHIP_CERTIFICATE;
      dvsCheckRequestModel.familyName = citizenshipCertificateDetails?.familyName;
      dvsCheckRequestModel.givenNames = citizenshipCertificateDetails?.givenNames;
      dvsCheckRequestModel.dateOfBirth = citizenshipCertificateDetails.dateOfBirth;
      dvsCheckRequestModel.acquisitionDate = citizenshipCertificateDetails.acquisitionDate;
      dvsCheckRequestModel.certificateNumber = citizenshipCertificateDetails.certificateNumber;

      return dvsCheckRequestModel;
    }
    if (values?.identityType === IDENTITY_TYPES.FORIGN_PASSPORT) {
      const foreignPassportDetails = identityDetails as IForeignPassportDetails;
      dvsCheckRequestModel.identityType = 'VI';
      dvsCheckRequestModel.passportNumber = foreignPassportDetails?.passportNumber ?? '';
      dvsCheckRequestModel.familyName = foreignPassportDetails?.familyName;
      dvsCheckRequestModel.givenNames = foreignPassportDetails?.givenNames;
      dvsCheckRequestModel.dateOfBirth = foreignPassportDetails.dateOfBirth;

      return dvsCheckRequestModel;
    }
    if (values?.identityType === IDENTITY_TYPES.IMMI_CARD) {
      const immiCardDetails = identityDetails as IImmiCardDetails;
      dvsCheckRequestModel.identityType = IDENTITY_TYPES.IMMI_CARD;
      dvsCheckRequestModel.familyName = immiCardDetails?.familyName;
      dvsCheckRequestModel.givenNames = immiCardDetails?.givenNames;
      dvsCheckRequestModel.dateOfBirth = immiCardDetails.dateOfBirth;
      dvsCheckRequestModel.immiCardNumber = immiCardDetails.immiCardNumber;

      return dvsCheckRequestModel;
    }

  };

  const trimSpacesFromNames = () => {
    //Trim names from the PersonalDetails
    if (values) {
      values.firstName = values.firstName?.trim();
      values.lastName = values.lastName?.trim();
      values.otherGivenNames = values.otherGivenNames?.trim();
    }

    //Trim names from the identitity documents
    if (values?.identityType === IDENTITY_TYPES.AU_PASSPORT && values?.auPassportDetails) {
      values.auPassportDetails.familyName = values.auPassportDetails.familyName?.trim();
      values.auPassportDetails.givenNames = values.auPassportDetails.givenNames?.trim();
    }

    if (values?.identityType === IDENTITY_TYPES.AU_BIRTH_CERTIFICATE && values?.auBirthCertificateDetails) {
      values.auBirthCertificateDetails.familyName = values.auBirthCertificateDetails.familyName?.trim();
      values.auBirthCertificateDetails.givenNames = values.auBirthCertificateDetails.givenNames?.trim();
    }

    if (values?.identityType === IDENTITY_TYPES.AU_CITIZENSHIP_CERTIFICATE && values?.auCitizenshipCertificateDetails) {
      values.auCitizenshipCertificateDetails.familyName = values.auCitizenshipCertificateDetails.familyName?.trim();
      values.auCitizenshipCertificateDetails.givenNames = values.auCitizenshipCertificateDetails.givenNames?.trim();
    }

    if (values?.identityType === IDENTITY_TYPES.FORIGN_PASSPORT && values?.foreignPassportDetails) {
      values.foreignPassportDetails.familyName = values.foreignPassportDetails.familyName?.trim();
      values.foreignPassportDetails.givenNames = values.foreignPassportDetails.givenNames?.trim();
    }

    if (values?.identityType === IDENTITY_TYPES.IMMI_CARD && values?.immiCardDetails) {
      values.immiCardDetails.familyName = values.immiCardDetails.familyName?.trim();
      values.immiCardDetails.givenNames = values.immiCardDetails.givenNames?.trim();
    }

  };
  const handleNext = async () => {
    trimSpacesFromNames();
    const validationErrors = await validateForm();
    if (!validationErrors || isEmpty(validationErrors)) {
      if (values?.identityType === IDENTITY_TYPES.FORIGN_PASSPORT
        && (values.foreignPassportDetails.isInAustralia === 'no' && values.foreignPassportDetails.isAuPermanentResident === 'no')) {
        setFormAlert(null);
        openStepById(props.nextStep);
        setPreviousStep('IdentificationDetails');
      }

      setOnChangeValidation(false);
      setCheckDvs(true);
    }
    else {
      setOnChangeValidation(true);
    }
  };

  if (isLoading) {
    return <Loader message='Working. Please wait.....' />;
  }

  const getIdentityDetails = () => {
    if (values?.identityType === IDENTITY_TYPES.AU_PASSPORT) {
      return <AustralianPassportDetails />;
    }
    if (values?.identityType === IDENTITY_TYPES.AU_BIRTH_CERTIFICATE) {
      return <AustralianBirthCertificateDetails />;
    }
    if (values?.identityType === IDENTITY_TYPES.AU_CITIZENSHIP_CERTIFICATE) {
      return <AustralianCitizenshipCertificateDetails />;
    }
    if (values?.identityType === IDENTITY_TYPES.FORIGN_PASSPORT) {
      return <ForeignPassportDetails handleNextAction={handleNext} />;
    }
    if (values?.identityType === IDENTITY_TYPES.IMMI_CARD) {
      return <ImmiCardDetails />;
    }
  };

  return (
    <>
      {getIdentityDetails()}
      {
        values?.identityType != IDENTITY_TYPES.FORIGN_PASSPORT &&
        <FormNavigation nextButtonText='Next' disableNextButton={disableNextBtn} hidePrevious onNextAction={handleNext} showSaveAndExit={false} />
      }
    </>
  );
};

export default IdentityDetails;